// plan-19 S04: parameterized PNG card render engine — bible §20, §21, §45
// renders a 1080x1080 card with title, header line, content (SVG or text), scanlines,
// and a stamp URL + qHashShort. self-contained: no fetches, no third-party libs.

export interface ContentRegion {
  x: number
  y: number
  w: number
  h: number
}

export type CardContent =
  | { kind: 'svg'; svg: SVGElement }
  | { kind: 'text'; render: (ctx: CanvasRenderingContext2D, region: ContentRegion) => Promise<void> | void }

export interface CardSpec {
  title: string         // e.g., "HEATMAP.EXE // CONSENSUS GRID"
  headerLine: string    // e.g., '> consensus: "king"'
  content: CardContent
  stampUrl: string      // e.g., "vaguepostking.com/#heatmap=king"
  stampHash: string     // e.g., "@a3f2"
  filename: string      // e.g., "vpk-consensus-king-a3f2.png"
}

const CARD_W = 1080
const CARD_H = 1080
const BORDER_INSET = 36
const CONTENT_REGION: ContentRegion = { x: 140, y: 200, w: 800, h: 740 }

// palette (mirrors tokens.css — kept inline so the engine is portable + deterministic)
const COLOR_BG = '#060A0C'
const COLOR_GREEN_DIM = '#005A00'
const COLOR_KING_GREEN = '#00B400'
const COLOR_AMBER = '#FFB000'

const FONT_FAMILY = '"IBM Plex Mono", monospace'

async function loadFonts(): Promise<void> {
  if (!document.fonts) return
  try {
    await Promise.all([
      document.fonts.load(`14px ${FONT_FAMILY}`),
      document.fonts.load(`18px ${FONT_FAMILY}`),
      document.fonts.load(`22px ${FONT_FAMILY}`),
      document.fonts.load(`500 18px ${FONT_FAMILY}`),
    ])
  } catch {
    // font load failures fall back to monospace; layout stays intact
  }
}

function paintBackground(ctx: CanvasRenderingContext2D): void {
  ctx.fillStyle = COLOR_BG
  ctx.fillRect(0, 0, CARD_W, CARD_H)
}

function paintBorder(ctx: CanvasRenderingContext2D): void {
  ctx.strokeStyle = COLOR_GREEN_DIM
  ctx.lineWidth = 1
  ctx.strokeRect(
    BORDER_INSET + 0.5,
    BORDER_INSET + 0.5,
    CARD_W - BORDER_INSET * 2 - 1,
    CARD_H - BORDER_INSET * 2 - 1,
  )
}

function paintTitle(ctx: CanvasRenderingContext2D, title: string): void {
  ctx.fillStyle = COLOR_GREEN_DIM
  ctx.font = `500 18px ${FONT_FAMILY}`
  ctx.textBaseline = 'alphabetic'
  ctx.textAlign = 'left'
  // letter-spacing 1.4px emulated via per-char draw
  let x = 72
  const y = 84
  for (const ch of title) {
    ctx.fillText(ch, x, y)
    x += ctx.measureText(ch).width + 1.4
  }
}

function paintHeaderLine(ctx: CanvasRenderingContext2D, headerLine: string): void {
  ctx.fillStyle = COLOR_KING_GREEN
  ctx.font = `22px ${FONT_FAMILY}`
  ctx.textBaseline = 'alphabetic'
  ctx.textAlign = 'left'
  // truncate if extremely long — keep on one line
  const max = CARD_W - 144
  let text = headerLine
  while (ctx.measureText(text).width > max && text.length > 8) {
    text = text.slice(0, -1)
  }
  if (text !== headerLine) text = text.slice(0, -1) + '…'
  ctx.fillText(text, 72, 132)
}

async function paintSvgContent(ctx: CanvasRenderingContext2D, svg: SVGElement): Promise<void> {
  // clone + ensure xmlns is present so the serialized string is a valid standalone SVG
  const clone = svg.cloneNode(true) as SVGElement
  if (!clone.getAttribute('xmlns')) {
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  }
  const xml = new XMLSerializer().serializeToString(clone)
  // base64-encode (handles unicode via percent-encoding round-trip)
  const b64 = btoa(unescape(encodeURIComponent(xml)))
  const dataUrl = `data:image/svg+xml;base64,${b64}`

  const img = new Image()
  img.src = dataUrl
  await img.decode()

  // center the SVG within the content region preserving aspect
  const r = CONTENT_REGION
  const aspect = img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 1
  let dw = r.w
  let dh = r.w / aspect
  if (dh > r.h) {
    dh = r.h
    dw = r.h * aspect
  }
  const dx = r.x + (r.w - dw) / 2
  const dy = r.y + (r.h - dh) / 2
  ctx.drawImage(img, dx, dy, dw, dh)
}

async function paintTextContent(
  ctx: CanvasRenderingContext2D,
  render: (ctx: CanvasRenderingContext2D, region: ContentRegion) => Promise<void> | void,
): Promise<void> {
  ctx.save()
  // clip so the renderer can't bleed outside its region
  ctx.beginPath()
  ctx.rect(CONTENT_REGION.x, CONTENT_REGION.y, CONTENT_REGION.w, CONTENT_REGION.h)
  ctx.clip()
  await render(ctx, CONTENT_REGION)
  ctx.restore()
}

function paintScanlines(ctx: CanvasRenderingContext2D): void {
  ctx.fillStyle = 'rgba(0, 0, 0, 0.12)'
  for (let y = 0; y < CARD_H; y += 3) {
    ctx.fillRect(0, y, CARD_W, 1)
  }
}

function paintStamp(ctx: CanvasRenderingContext2D, stampUrl: string, stampHash: string): void {
  ctx.textBaseline = 'alphabetic'
  ctx.textAlign = 'right'
  const xRight = CARD_W - 72

  // URL line — amber, 18px
  ctx.fillStyle = COLOR_AMBER
  ctx.font = `18px ${FONT_FAMILY}`
  // truncate URL if it would extend past the left bound
  const maxUrlW = CARD_W - 144
  let urlText = stampUrl
  while (ctx.measureText(urlText).width > maxUrlW && urlText.length > 12) {
    urlText = urlText.slice(0, -1)
  }
  if (urlText !== stampUrl) urlText = urlText.slice(0, -1) + '…'
  ctx.fillText(urlText, xRight, 1010)

  // qHash line — dim, 14px, 8px below
  ctx.fillStyle = COLOR_GREEN_DIM
  ctx.font = `14px ${FONT_FAMILY}`
  ctx.fillText(stampHash, xRight, 1034)
}

export async function exportCard(spec: CardSpec): Promise<Blob> {
  await loadFonts()

  const canvas = document.createElement('canvas')
  canvas.width = CARD_W
  canvas.height = CARD_H
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas 2d context unavailable')

  paintBackground(ctx)
  paintBorder(ctx)
  paintTitle(ctx, spec.title)
  paintHeaderLine(ctx, spec.headerLine)

  if (spec.content.kind === 'svg') {
    await paintSvgContent(ctx, spec.content.svg)
  } else {
    await paintTextContent(ctx, spec.content.render)
  }

  paintScanlines(ctx)
  paintStamp(ctx, spec.stampUrl, spec.stampHash)

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob)
      else reject(new Error('canvas toBlob failed'))
    }, 'image/png')
  })
}

// helper exported for callers that need it for filename/stamp construction
export function safeWord(word: string, maxLen = 24): string {
  return word.toLowerCase().replace(/[^a-z0-9]+/g, '').slice(0, maxLen)
}

// download a Blob via an in-document anchor — cleans up object URL after click
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // revoke after a tick so the browser has a chance to start the download
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

// shared canvas text-wrap helper: paints `text` starting at (x, y), wrapping at
// word boundaries to fit `maxW`, advancing y by `lineH` per line. returns the
// final y (after the last line) so callers can stack blocks.
export function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxW: number,
  lineH: number,
): number {
  const words = text.split(/\s+/)
  let line = ''
  let cursorY = y
  for (const w of words) {
    const candidate = line ? line + ' ' + w : w
    if (ctx.measureText(candidate).width > maxW && line) {
      ctx.fillText(line, x, cursorY)
      cursorY += lineH
      line = w
    } else {
      line = candidate
    }
  }
  if (line) {
    ctx.fillText(line, x, cursorY)
    cursorY += lineH
  }
  return cursorY
}
